// frontend/src/pages/Signup.tsx

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ResendVerificationButton } from '@/components/ui/ResendVerificationButton';

const LANGUAGES = [
  { value: 'en',    label: '🇬🇧 English' },
  { value: 'es-LA', label: '🇪🇸 Español' },
  { value: 'pt-BR', label: '🇧🇷 Português' },
];

// Cloudflare Turnstile CAPTCHA. If no site key is configured, the widget is skipped
// and signup isn't blocked (the backend only enforces it when its secret is set).
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export function Signup() {
  const { t, i18n } = useTranslation();
  const { signup } = useAuthStore();
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    language: i18n.language?.startsWith('pt') ? 'pt-BR'
             : i18n.language?.startsWith('es') ? 'es-LA'
             : 'en',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Load the Turnstile script once and render the widget while the form is shown
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || registeredEmail) return;

    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileContainer.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileContainer.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };

    let script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (window.turnstile) {
      renderWidget();
    } else {
      if (!script) {
        script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderWidget);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener('load', renderWidget);
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      turnstileWidgetId.current = null;
      setTurnstileToken('');
    };
  }, [registeredEmail]);

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken('');
  };

  // When user changes language selector, also switch the UI language
  const handleLanguageChange = (lang: string) => {
    setFormData({ ...formData, language: lang });
    i18n.changeLanguage(lang);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = t('auth.email_required');
    }
    if (!formData.username || formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwords_must_match');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      await signup({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        language: formData.language,
        turnstileToken,
      });
      toast.success(t('auth.signup_success'));
      setRegisteredEmail(formData.email);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Signup failed');
      // Turnstile tokens are single-use: get a fresh one for the next attempt
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  if (registeredEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <Zap className="w-8 h-8 text-apple-blue-500" />
            <span className="text-2xl font-semibold">{t('app_name')}</span>
          </Link>

          <Card>
            <CardHeader>
              <CardTitle className="text-center text-3xl">
                {t('auth.check_email_title')}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="text-center space-y-6">
                <p className="text-apple-gray-700">
                  {t('auth.check_email_body', { email: registeredEmail })}
                </p>
                <ResendVerificationButton email={registeredEmail} />
                <Link
                  to="/login"
                  className="inline-block text-apple-blue-500 hover:text-apple-blue-600 font-medium"
                >
                  {t('auth.back_to_login')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-8 h-8 text-apple-blue-500" />
          <span className="text-2xl font-semibold">{t('app_name')}</span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-3xl">
              {t('auth.create_account')}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Language selector — shown first so UI switches immediately */}
              <div>
                <label className="block text-sm font-medium text-apple-gray-700 mb-1">
                  {t('profile.language')}
                </label>
                <div className="flex gap-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.value}
                      type="button"
                      onClick={() => handleLanguageChange(lang.value)}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        formData.language === lang.value
                          ? 'border-apple-blue-500 bg-apple-blue-50 text-apple-blue-700'
                          : 'border-apple-gray-200 text-apple-gray-600 hover:border-apple-gray-400'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              <Input
                label={t('auth.email')}
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={errors.email}
                autoComplete="email"
              />

              <Input
                label={t('profile.username')}
                type="text"
                placeholder="satoshi"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                error={errors.username}
                autoComplete="username"
              />

              <Input
                label={t('auth.password')}
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={errors.password}
                autoComplete="new-password"
                helperText="Minimum 8 characters"
              />

              <Input
                label={t('auth.confirm_password')}
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                error={errors.confirmPassword}
                autoComplete="new-password"
              />

              {TURNSTILE_SITE_KEY && (
                <div ref={turnstileContainer} className="flex justify-center" />
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isLoading}
                disabled={!!TURNSTILE_SITE_KEY && !turnstileToken}
              >
                {t('auth.create_account')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-apple-gray-600">
                {t('auth.already_have_account')}{' '}
              </span>
              <Link
                to="/login"
                className="text-apple-blue-500 hover:text-apple-blue-600 font-medium"
              >
                {t('auth.login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

